"use client";

/**
 * RunExperience — React shell for "The Run" act-based scroll experience.
 *
 * Ownership split (keep it this way):
 *   - THIS component owns scroll: one master ScrollTrigger + one scrubbed
 *     panel timeline + act snapping. It feeds the engine a single number.
 *   - run-engine.ts owns the WebGL world and its own easing. It never
 *     touches ScrollTrigger.
 *   - LenisProvider (already in the app shell) owns the clock: Lenis runs
 *     on gsap.ticker; ScrollTrigger.update is bridged there. Nothing here
 *     creates a rAF loop.
 *
 * The heavy engine chunk is dynamic-imported after hydration, so the act
 * copy below is server-rendered, crawlable HTML long before three.js lands.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { gsap, ScrollTrigger, registerGsap } from "@/lib/gsap-setup";
import { WolfMark } from "@/components/brand/Logo";
import { localeHref, isLocale } from "@/lib/i18n/config";
import type { GalleryItem, GalleryStatItem, LandingContent, ProjectDatum } from "../content";
import { expAsset } from "../assets";
import type { RunCopy } from "./run-content";
import type { TheRunEngine } from "./run-engine";
import { RunDialogs, type RunDialogsHandle } from "./RunDialogs";
import "./run.css";

/** Scroll rail length. More rail = slower, more cinematic scrub.
 *  NOTE: the master trigger runs "top top" → "bottom bottom", so the actual
 *  scrollable distance is RAIL_VH - 100vh = 520vh. */
const RAIL_VH = 620;

/** Act boundaries — MUST match ACT_BOUNDS in run-engine.ts. */
const BOUNDS = [0, 0.18, 0.34, 0.54, 0.84, 1];

/** Panel in/out positions on the master timeline (fractions of progress).
 *  Every window must stay strictly INSIDE its own act: under reduced motion the
 *  panels are driven by ACT_BOUNDS alone (onActChange below), so a window that
 *  straddled a bound would make the two paths disagree. */
const PANEL_WINDOWS: [number, number, number, number][] = [
  // [fadeInStart, fadeInEnd, fadeOutStart, fadeOutEnd]
  [0.0, 0.015, 0.115, 0.165], // ignition
  [0.2, 0.24, 0.3, 0.335], // run
  [0.36, 0.4, 0.5, 0.535], // forge
  [0.56, 0.6, 0.8, 0.835], // gallery
  [0.86, 0.91, 1.0, 1.0], // summit (never exits)
];

/** Snap targets = the progress at which each act's panel is FULLY faded in.
 *  NOT the act bounds: a bound sits in the blank gap between two panels, so
 *  snapping there parks the reader on wordless 3D. Act I is excluded — it
 *  scrubs freely below BOUNDS[1]. */
const SNAP_P = PANEL_WINDOWS.slice(1).map((w) => w[1]); // [0.24, 0.40, 0.60, 0.91]
/** Snap capture radius in progress units. 0.055 × 520vh = 28.6vh, matching
 *  the previous felt band (0.045 × 640vh = 28.8vh). Tightest target gap is
 *  0.16, so half-gap 0.08 > 0.055 — no overlapping basins. */
const SNAP_D = 0.055;

export interface RunExperienceProps {
  copy: RunCopy;
  /** The in-house apps — powers the modal browser and the site index. */
  projects: ProjectDatum[];
  /** The scrolling card sequence: apps interleaved with studio stat cards. */
  gallery: GalleryItem[];
  dir: "ltr" | "rtl";
  /** Shared studio content (brand wordmark + modal/form/contact copy). */
  content: LandingContent;
  /** Locale-aware href for the header brand link. */
  homeHref: string;
  locale: string;
}

export function RunExperience({ copy, projects, gallery, dir, content, homeHref, locale }: RunExperienceProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const panelRefs = useRef<(HTMLElement | null)[]>([]);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const captionMainRef = useRef<HTMLSpanElement>(null);
  const captionStatusRef = useRef<HTMLSpanElement>(null);
  const captionBlurbRef = useRef<HTMLSpanElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const loadPctRef = useRef<HTMLSpanElement>(null);
  const loadBarRef = useRef<HTMLDivElement>(null);
  const loadStatusRef = useRef<HTMLDivElement>(null);
  const dialogsRef = useRef<RunDialogsHandle | null>(null);
  const [fallback, setFallback] = useState(false);
  const loc = isLocale(locale) ? locale : "en";
  // stable identity — a fresh array each render would re-run RunDialogs'
  // hashchange/deep-link effects on unrelated re-renders (e.g. onFallback)
  const stats = useMemo(
    () => gallery.filter((i): i is GalleryStatItem => i.type === "stat"),
    [gallery],
  );

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    let engine: TheRunEngine | null = null;
    let master: ScrollTrigger | null = null;
    let tl: gsap.core.Timeline | null = null;
    let hudTimer = 0;
    let killed = false;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const debug = window.location.search.includes("run=debug");

    // ── boot overlay: milestone-driven, engine reports progress ──
    let loadPct = 0;
    let loaderDone = false;
    const setLoad = (pct: number, phase?: string) => {
      loadPct = Math.max(loadPct, pct);
      if (loadPctRef.current) loadPctRef.current.textContent = String(Math.round(loadPct));
      if (loadBarRef.current) loadBarRef.current.style.width = `${loadPct}%`;
      if (phase && loadStatusRef.current) loadStatusRef.current.textContent = phase;
    };
    const finishLoader = () => {
      if (loaderDone) return;
      loaderDone = true;
      window.clearTimeout(loaderSafety);
      setLoad(100, "Ready — scroll to run");
      const ov = loaderRef.current;
      if (!ov) return;
      if (reduced) {
        ov.style.display = "none";
        return;
      }
      gsap.timeline({ delay: 0.35, onComplete: () => { ov.style.display = "none"; } })
        .to(ov.querySelectorAll("[data-lg]"), { yPercent: -130, opacity: 0, filter: "blur(5px)", duration: 0.5, stagger: 0.045, ease: "power3.in" }, 0)
        .to(ov.querySelector("[data-ring]"), { scale: 0.22, opacity: 0, duration: 0.55, ease: "power3.in" }, 0.05)
        .to(ov.querySelector("[data-meta2]"), { opacity: 0, y: 14, duration: 0.35 }, 0)
        .to(ov, { clipPath: "inset(0 0 100% 0)", duration: 0.75, ease: "power4.inOut" }, 0.4);
    };
    // a stalled asset must never trap the user on the overlay
    const loaderSafety = window.setTimeout(finishLoader, 12000);
    setLoad(6, "Boot sequence");

    const onResize = () => engine?.resize();

    (async () => {
      const mod = await import("./run-engine");
      if (killed) return;
      registerGsap();
      setLoad(18, "Motion system · GSAP + Lenis");

      engine = new mod.TheRunEngine({
        canvas,
        modelUrl: expAsset("ab-wolf.glb"),
        envUrl: expAsset("env-studio.jpg"),
        cards: gallery.slice(0, 9).map((it) =>
          it.type === "app"
            ? {
                thumb: it.project.thumb,
                accent: it.project.statusColor,
                // breathe = unreleased only; BETA holds steady like shipped work
                pending: it.project.status === "PRE_LAUNCH",
                no: it.project.no,
                name: it.project.name,
                statusLabel: it.project.statusLabel,
                // identity-plate copy, used only when the app has no capture
                sub: it.project.kind,
                note: it.project.blurb,
                interactive: true,
              }
            : {
                thumb: "",
                accent: it.accent,
                pending: false,
                no: it.no,
                name: it.label,
                // taxonomy tag, not a status — the plate says what KIND of
                // card this is, while the click affordance stays uniform
                statusLabel: content.modal.index,
                statValue: it.value,
                sub: it.label,
                note: it.note,
                // stat cards open their own breakdown dialog
                interactive: true,
              },
        ),
        reducedMotion: reduced,
        onActChange: (act) => {
          root.setAttribute("data-act", String(act));
          if (reduced) {
            // no scrub timeline under reduced motion — CSS shows the act panel
            panelRefs.current.forEach((el, i) => {
              if (el) el.classList.toggle("is-active", i === act);
            });
          }
        },
        onWorkFocus: (idx) => {
          const cap = captionRef.current;
          if (!cap) return;
          const it = idx < 0 ? null : gallery[idx];
          if (!it) {
            cap.style.opacity = "0";
            return;
          }
          // Timeline and status ride along with the name so the corridor
          // states when a product shipped without opening anything.
          // U+2066/U+2069 isolate ordinals and year ranges so neither
          // reorders inside the Arabic (RTL) caption.
          if (it.type === "app") {
            const p = it.project;
            if (captionMainRef.current) {
              captionMainRef.current.textContent =
                `⁦${p.no}⁩ — ${p.name} · ${p.kind} · ⁦${p.year}⁩`;
            }
            if (captionStatusRef.current) {
              captionStatusRef.current.textContent = p.statusLabel;
              captionStatusRef.current.style.color = p.statusColor;
            }
            if (captionBlurbRef.current) captionBlurbRef.current.textContent = p.blurb;
          } else {
            if (captionMainRef.current) {
              captionMainRef.current.textContent = `⁦${it.no}⁩ — ${it.label}`;
            }
            if (captionStatusRef.current) {
              captionStatusRef.current.textContent = it.value;
              captionStatusRef.current.style.color = it.accent;
            }
            if (captionBlurbRef.current) captionBlurbRef.current.textContent = it.note;
          }
          cap.style.opacity = "1";
        },
        onFallback: () => {
          finishLoader();
          setFallback(true);
        },
        onLoadProgress: (pct, phase) => setLoad(pct, phase),
        onReady: () => finishLoader(),
        // gallery card click → the matching showcase dialog, both deep-linked:
        // apps open the project modal (its "Visit site" link still opens
        // ProjectDatum.url), stat cards open their breakdown dialog.
        onWorkClick: (idx) => {
          const it = gallery[idx];
          if (it?.type === "app") dialogsRef.current?.openProject(it.appIndex);
          else if (it?.type === "stat") dialogsRef.current?.openStat(it.no);
        },
      });

      if (!engine.init()) return; // onFallback already fired
      engine.start();
      if (reduced) finishLoader(); // no exit choreography to wait for

      // ── master scroll: ONE trigger feeds the engine ──
      master = ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (st) => engine?.setTargetProgress(st.progress),
        snap: reduced
          ? undefined
          : {
              // Direction gating is done by hand: GSAP ignores snap.directional
              // whenever snapTo is a function (the _isFunction branch in
              // ScrollTrigger short-circuits it), so the old `directional: true`
              // never did anything. Without this a user scrolling back up out of
              // act II gets yanked forward again.
              snapTo: (value: number, st?: ScrollTrigger) => {
                if (value < BOUNDS[1]) return value; // act I scrubs freely
                const dir = st?.direction ?? 0;
                let best = value;
                let bestD = SNAP_D;
                for (const b of SNAP_P) {
                  if (dir > 0 && b < value - 0.002) continue; // never pull backward
                  if (dir < 0 && b > value + 0.002) continue; // never pull forward
                  const d = Math.abs(value - b);
                  if (d < bestD) { bestD = d; best = b; }
                }
                return best;
              },
              duration: { min: 0.25, max: 0.7 },
              ease: "power2.inOut",
            },
      });

      // ── panel choreography: one scrubbed timeline, positions = progress ──
      if (!reduced) {
        tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.4 },
        });
        // Pin the timeline's duration to exactly 1 so PANEL_WINDOWS positions
        // map 1:1 onto scroll progress. The last window ends at 0.91; without
        // this the scrub compresses every window by that factor (panels run
        // ~7% early and the summit only fully lands at the very last pixel).
        tl.set({}, {}, 1);
        PANEL_WINDOWS.forEach(([i0, i1, o0, o1], idx) => {
          const el = panelRefs.current[idx];
          if (!el) return;
          tl!.fromTo(
            el,
            { autoAlpha: 0, y: 36 },
            { autoAlpha: 1, y: 0, duration: Math.max(0.005, i1 - i0) },
            i0
          );
          if (o1 > o0 && o0 < 1) {
            tl!.to(el, { autoAlpha: 0, y: -26, duration: Math.max(0.005, o1 - o0) }, o0);
          }
        });
      } else {
        panelRefs.current.forEach((el, i) => el?.classList.toggle("is-active", i === 0));
      }

      window.addEventListener("resize", onResize);

      if (debug && hudRef.current) {
        hudRef.current.style.display = "block";
        hudTimer = window.setInterval(() => {
          if (!engine || !hudRef.current) return;
          const s = engine.getState();
          hudRef.current.textContent = `act ${s.act + 1} · p ${s.progress.toFixed(3)} · ${s.fps} fps · tier ${s.tier} · ${s.modelReady ? "glb" : "loading"}`;
        }, 300);
      }
    })();

    return () => {
      killed = true;
      window.removeEventListener("resize", onResize);
      window.clearTimeout(loaderSafety);
      if (hudTimer) window.clearInterval(hudTimer);
      tl?.scrollTrigger?.kill();
      tl?.kill();
      master?.kill();
      engine?.dispose();
      engine = null;
    };
    // mount-once: copy/projects/gallery are per-locale route props, dir is static per locale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPanel = (i: number) => (el: HTMLElement | null) => {
    // eslint-disable-next-line react-hooks/refs -- ref callback: React invokes it at commit, not during render
    panelRefs.current[i] = el;
  };

  return (
    <>
    <div ref={rootRef} className="run-root" dir={dir} style={{ height: `${RAIL_VH}vh` }} data-act="0">
      <a className="run-skip" href="#after-run">{copy.skip}</a>

      {/* brand header — mirrors the landing experience's fixed header (same
          WolfMark + wordmark + mono meta treatment) so the brand carries
          into The Run unchanged */}
      <header className="run-header">
        <a className="run-brand" href={homeHref}>
          <WolfMark variant="sm" priority className="run-brand-mark" />
          <span className="run-brand-word">{content.brand}</span>
        </a>
        <div className="run-header-meta" aria-hidden="true">
          <span className="run-header-dot" />
          <span>TIRANA</span>
          <span className="run-header-year">· 2026</span>
        </div>
      </header>

      {/* boot overlay — milestone-driven by the engine, exits via gsap wipe.
          Diagnostic strings stay English on purpose (dev-facing microcopy). */}
      <div ref={loaderRef} className="run-loader" aria-busy="true" aria-label="Loading experience">
        <div className="run-loader-blob run-loader-blob--a" aria-hidden="true" />
        <div className="run-loader-blob run-loader-blob--b" aria-hidden="true" />
        <div className="run-loader-scan" aria-hidden="true" />
        <div className="run-loader-word" aria-hidden="true">
          {"AMALBRYX".split("").map((ch, i) => (
            <span key={i} data-lg="1" style={{ animationDelay: `${i * 0.12}s` }}>{ch}</span>
          ))}
        </div>
        <div className="run-loader-ring" data-ring="1">
          <div className="run-loader-ring-sweep" />
          <div className="run-loader-ring-hole" />
          <div className="run-loader-ring-dash" />
          <div className="run-loader-ring-pct"><span ref={loadPctRef}>0</span><em>%</em></div>
        </div>
        <div className="run-loader-meta" data-meta2="1">
          <div className="run-loader-bar"><div ref={loadBarRef} /></div>
          <div ref={loadStatusRef} className="run-loader-status">Boot sequence</div>
          <div className="run-loader-fine">Immersive experience · three · Draco · GSAP</div>
        </div>
      </div>

      {!fallback ? (
        <canvas ref={canvasRef} className="run-canvas" aria-hidden="true" />
      ) : (
        <div className="run-fallback" aria-hidden="true" />
      )}

      <div className="run-stage" aria-live="off">
        {/* ACT I — IGNITION */}
        <section ref={setPanel(0)} className="run-panel run-panel--center" data-screen-label="Run 01 Ignition">
          <p className="run-eyebrow">{copy.ignition.eyebrow}</p>
          <h1 className="run-title">
            {copy.ignition.title} <em>{copy.ignition.titleAlt}</em>
          </h1>
          {copy.ignition.body ? <p className="run-body">{copy.ignition.body}</p> : null}
          <p className="run-hint" aria-hidden="true">
            <span className="run-hint-line" />
            {copy.scrollHint}
          </p>
        </section>

        {/* ACT II — THE RUN */}
        <section ref={setPanel(1)} className="run-panel run-panel--side" data-screen-label="Run 02 The Run">
          <p className="run-eyebrow">{copy.run.eyebrow}</p>
          <h2 className="run-title run-title--md">
            {copy.run.title} <em>{copy.run.titleAlt}</em>
          </h2>
          <p className="run-stat">{copy.run.stat}</p>
        </section>

        {/* ACT III — THE FORGE */}
        <section ref={setPanel(2)} className="run-panel run-panel--side run-panel--start" data-screen-label="Run 03 Forge">
          <p className="run-eyebrow">{copy.forge.eyebrow}</p>
          <h2 className="run-title run-title--md">
            {copy.forge.title} <em>{copy.forge.titleAlt}</em>
          </h2>
          <ul className="run-list">
            {copy.forge.disciplines.map((d, i) => (
              <li key={d}>
                <span className="run-list-no">{String(i + 1).padStart(2, "0")}</span> {d}
              </li>
            ))}
          </ul>
        </section>

        {/* ACT IV — GALLERY */}
        <section ref={setPanel(3)} className="run-panel run-panel--side" data-screen-label="Run 04 Gallery">
          <p className="run-eyebrow">{copy.gallery.eyebrow}</p>
          <h2 className="run-title run-title--md">
            {copy.gallery.title} <em>{copy.gallery.titleAlt}</em>
          </h2>
        </section>

        {/* ACT V — SUMMIT */}
        <section ref={setPanel(4)} className="run-panel run-panel--center" data-screen-label="Run 05 Summit">
          <p className="run-eyebrow">{copy.summit.eyebrow}</p>
          <h2 className="run-title">
            {copy.summit.title} <em>{copy.summit.titleAlt}</em>
          </h2>
          {copy.summit.body ? <p className="run-body">{copy.summit.body}</p> : null}
          <div className="run-cta-row">
            <button type="button" className="run-cta" onClick={() => dialogsRef.current?.openForm()}>
              {content.contact.ctaPrimary} →
            </button>
            <a className="run-cta run-cta--ghost" href={copy.summit.ctaHref}>{copy.summit.cta} →</a>
            <a className="run-cta run-cta--ghost" href={copy.summit.altHref}>{copy.summit.alt} →</a>
          </div>
        </section>

        {/* live gallery caption — engine-driven, no React re-render */}
        <p ref={captionRef} className="run-caption" aria-hidden="true">
          <span className="run-caption-row">
            <span ref={captionMainRef} />
            <span ref={captionStatusRef} className="run-caption-status" />
            {/* click affordance — every card opens a dialog now, so the chip
                rides the caption unconditionally. cursor.open is localized;
                the arrow mirrors under RTL like every other arrow on the site. */}
            <span className="run-caption-open">
              {content.cursor.open} <span aria-hidden className="arrow-rtl-flip">↗</span>
            </span>
          </span>
          <span ref={captionBlurbRef} className="run-caption-blurb" />
        </p>
      </div>

      <div ref={hudRef} className="run-hud" aria-hidden="true" />
      <span id="work" className="run-anchor run-anchor--work" />

      <RunDialogs
        content={content}
        projects={projects}
        stats={stats}
        dir={dir}
        locale={locale}
        handleRef={dialogsRef}
      />
    </div>

    {/* Site index — the destination of the skip link, and the homepage's only
        crawlable internal links: the gallery is a WebGL canvas, so without this
        the raw HTML offers no path to /case/*, /notes, /services or /contact.
        Server-rendered and genuinely visible (not a hidden SEO block) — it is
        what a visitor who skips the experience lands on. Deliberately a SIBLING
        of .run-root: that element's height IS the ScrollTrigger rail, so
        nesting this inside it would lengthen the rail and shift every act. */}
      <footer id="after-run" className="run-index" dir={dir}>
        <nav className="run-index-inner" aria-label={content.siteIndex.workHeading}>
          <div className="run-index-col">
            <h2 className="run-index-h">{content.siteIndex.workHeading}</h2>
            <ul className="run-index-list run-index-list--work">
              {projects.map((p) => (
                <li key={p.slug}>
                  <a href={localeHref(loc, `/case/${p.slug}`)}>
                    <span className="run-index-no">{p.no}</span>
                    {p.name}
                    <span className="run-index-kind">{p.kind}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="run-index-col">
            <h2 className="run-index-h">{content.siteIndex.studioHeading}</h2>
            <ul className="run-index-list">
              <li><a href={localeHref(loc, "/services")}>{content.siteIndex.services}</a></li>
              <li><a href={localeHref(loc, "/notes")}>{content.siteIndex.notes}</a></li>
              <li><a href={localeHref(loc, "/contact")}>{content.siteIndex.contact}</a></li>
              <li><a href={localeHref(loc, "/privacy")}>{content.siteIndex.privacy}</a></li>
              <li><a href={localeHref(loc, "/terms")}>{content.siteIndex.terms}</a></li>
              <li><a href={`mailto:${content.contact.email}`}>{content.contact.email}</a></li>
            </ul>
          </div>
        </nav>
        <p className="run-index-foot">{content.contact.footer}</p>
      </footer>
    </>
  );
}
